import { authenticatedFetch } from "./apiClient";
import { parseJsonOrThrow } from "../utils/apiRequestError";
import { STRATEGY_RD_API_BASE } from "../types/strategyRd";

/** One saved backtest as the Lab library shows it (light: no trades, no candles). */
export type StrategyRdLibraryItem = {
  id: string;
  name: string;
  /** Versions of one idea share a root (the first backtest). */
  root_id: string;
  version: number;
  created_by: "tes" | "user" | string;
  created_at: string | null;
  updated_at: string | null;
  symbol: string | null;
  candle_type: string | null;
  /** Trades in its last backtest, if it ran. */
  trades: number | null;
  /** Set once it was saved as a Strategy. */
  strategy_id: string | null;
  pinned: boolean;
};

export type StrategyRdLibraryAction = "pin" | "unpin" | "delete";

function str(v: unknown): string | null {
  return typeof v === "string" && v.trim() ? v.trim() : null;
}

export function parseLibraryItem(raw: unknown): StrategyRdLibraryItem | null {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, any>;
  const id = str(r.id);
  if (!id) return null;
  const scan = (r.scan_context ?? {}) as Record<string, unknown>;
  const last = (r.last_apply ?? null) as Record<string, unknown> | null;
  const count = last && typeof last.match_count === "number" ? last.match_count : null;
  return {
    id,
    name: str(r.name) ?? id,
    root_id: str(r.root_id) ?? id,
    version: typeof r.version === "number" && r.version > 0 ? r.version : 1,
    created_by: str(r.created_by) ?? "user",
    created_at: str(r.created_at),
    updated_at: str(r.updated_at),
    symbol: str(scan.symbol),
    candle_type: str(scan.candle_type),
    trades: count,
    strategy_id: str(r.report_strategy_hint?.report_strategy_id),
    pinned: Boolean(r.pinned),
  };
}

/** Every saved backtest (the library filters and groups them). */
export async function listStrategyRdLibrary(signal?: AbortSignal): Promise<StrategyRdLibraryItem[]> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/hypotheses`, { signal });
  const body = await parseJsonOrThrow<{ hypotheses?: unknown[] }>(response, "Could not load saved backtests.");
  return (Array.isArray(body.hypotheses) ? body.hypotheses : [])
    .map(parseLibraryItem)
    .filter((x): x is StrategyRdLibraryItem => x != null);
}

/** Pin / unpin / delete several backtests at once. Delete is permanent. */
export async function bulkStrategyRdLibrary(
  ids: string[],
  action: StrategyRdLibraryAction
): Promise<{ count: number; ids: string[] }> {
  const response = await authenticatedFetch(`${STRATEGY_RD_API_BASE}/hypotheses/bulk`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ ids, action }),
  });
  const body = await parseJsonOrThrow<{ count?: number; ids?: string[] }>(response, "Could not update the backtests.");
  return { count: body.count ?? 0, ids: Array.isArray(body.ids) ? body.ids : [] };
}

/** One idea: its versions newest first, pinned / newest ideas on top. */
export type StrategyRdLibraryFamily = {
  root_id: string;
  name: string;
  items: StrategyRdLibraryItem[];
  pinned: boolean;
  updated_at: string;
};

export function groupLibrary(items: StrategyRdLibraryItem[]): StrategyRdLibraryFamily[] {
  const by = new Map<string, StrategyRdLibraryItem[]>();
  for (const it of items) {
    by.set(it.root_id, [...(by.get(it.root_id) ?? []), it]);
  }
  const ts = (x: StrategyRdLibraryItem) => x.updated_at ?? x.created_at ?? "";
  return Array.from(by.entries())
    .map(([root_id, list]) => {
      const sorted = [...list].sort((a, b) => b.version - a.version || ts(b).localeCompare(ts(a)));
      const root = list.find((x) => x.id === root_id) ?? sorted[sorted.length - 1];
      return {
        root_id,
        name: root.name,
        items: sorted,
        pinned: list.some((x) => x.pinned),
        updated_at: list.map(ts).sort().pop() ?? "",
      };
    })
    .sort((a, b) => Number(b.pinned) - Number(a.pinned) || b.updated_at.localeCompare(a.updated_at));
}
